import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { ProductApi, CurrencyObject } from 'common/types';
import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization';
import { Link as RRLink } from 'react-router-dom';
import { Routes } from 'routing';

import { getFromPriceString } from 'common/utils/pricing/common';
import { getProductImageSrc } from 'common/utils/productUtils';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getProductTags } from 'services/products';
import TagGroup from './TagGroup';

interface Props {
	product: ProductApi;
	currency: CurrencyObject;
}
const ProductRow = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();

	const { product, currency } = props;
	const pricingTables = useSelector(ShopSelectors.activeShopPricingTables);
	const openingHoursWithExceptions = useSelector(
		ShopSelectors.activeLocationOpeningHoursWithException,
	);
	const locationCount = useSelector(ShopSelectors.allShopLocationsCount);
	const productImage = getProductImageSrc(product);

	return (
		<ContainerBox padding="none">
			<RRLink className={classes.wrapper} to={Routes.ProductPage(product.id)}>
				<Box className={classes.imageWrapper} data-qa="productRow">
					{!!productImage ? (
						<img className={classes.image} src={productImage} alt={product.name.def} />
					) : (
						<div className={classes.imagePlaceholder} />
					)}
				</Box>
				<Box className={classes.detailsWrapper}>
					<Typography variant="h6" align="left">
						{product.name.def}
					</Typography>
					<Typography variant="body2" align="left">
						{getFromPriceString(
							{
								product,
								pricingTables,
								openingHoursWithExceptions,
								currency,
							},
							t,
						)}
					</Typography>
					<TagGroup tags={getProductTags(product, locationCount, t)} />
				</Box>
			</RRLink>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'flex-start',
			alignItems: 'flex-start',
			width: '100%',
			padding: theme.spacing(4),
			[theme.breakpoints.down('md')]: {
				padding: theme.spacing(2),
			},
		},
		imageWrapper: {
			width: '80px',
			height: '80px',
		},
		image: {
			width: '100%',
			height: '100%',
			objectFit: 'contain',
		},
		imagePlaceholder: {
			width: '100%',
			height: '100%',
			background: theme.palette.background.secondary,
		},
		detailsWrapper: {
			marginLeft: theme.spacing(4),
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
		},
		actionsWrapper: {
			display: 'flex',
			flexDirection: 'row',
			alignSelf: 'stretch',
			alignItems: 'center',
		},
		sharedTag: {
			marginTop: theme.spacing(2),
			padding: '4px 12px',
			background: theme.palette.primary.light,
			borderRadius: '16px',
		},
		sharedTagText: {
			fontSize: '11px',
			lineHeight: '11px',
			fontWeight: 500,
		},
	}),
);

export default ProductRow;
